import React from 'react';
import styled from 'astroturf/react';

import routes from 'sitewide-js/core_pre_webpack/routes';
import { Body } from 'js/design_system/Body';
import { Button } from 'js/design_system/Button';
import { Modal, ModalButtonGroup } from 'js/design_system/Modal';
import { TextArea } from 'js/design_system/forms/TextArea';
import { useToast } from 'js/design_system/toasts/hooks';
import { TextButton } from 'js/design_system/TextButton';
import UserAvatar from 'js/liswp/sidebar_ds/UserAvatar';
import { User } from 'js/liswp/Sidebar/hooks/types';
import { ReaderMessageContext } from 'js/liswp/work_actions/ReaderMessageContext';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import { WorkId } from 'js/liswp/types';

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Prompt = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

// TODO: Reconcile with other, slightly incompatible source lists elsewhere, then enable
// type ReaderMessageSource =
//   | 'unknown'
//   | 'sutd'
//   | 'swp-toolbar'
//   | 'swp-preview-selector-dropdown'
//   | 'news'
//   | 'news-feed'
//   | 'search-results'
//   | 'history'
//   | 'sidebar'
//   | 'work_strip'
//   | 'profile'
//   | 'swp_mentions_sidebar'
//   | 'swp_related_works_sidebar'
//   | 'swp-bulk-download-modal'
//   | 'swp-related-works-grid'
//   | 'library'
//   | 'coronavirus_page'
//   | 'single-article-page';

interface MessageModalProps {
  primaryAuthor: User | undefined;
  onClose: () => void;
  workId: WorkId;
  attachmentId: number;
  readerMessageSource: string;
  reactionPrompt?: string;
}

export const ReaderMessageModal: React.FC<MessageModalProps> = ({
  primaryAuthor,
  onClose,
  workId,
  attachmentId,
  readerMessageSource,
  reactionPrompt,
}) => {
  const { setReaderMessageLimitReached } =
    React.useContext(ReaderMessageContext);
  const [message, setMessage] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const { infoToast, errorToast } = useToast();

  // Safety net: close modal if no author (shouldn't happen, but prevents crashes)
  if (!primaryAuthor) {
    onClose();
    return null;
  }

  const sendReaderMessage = async () => {
    if (sending) {
      return;
    }

    setSending(true);

    onClose();

    // We only hit the endpoint to check the limit on page load, so this is required
    // to update the limit on the page after a message is sent.
    // (e.g. in case of multiple downloads in one page load)
    setReaderMessageLimitReached(true);

    if (message.trim().length === 0) {
      return;
    }

    try {
      const response = await fetchWithCsrf(
        routes.api_v0_reader_messages_path({
          subdomain_param: 'api',
        }),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            work_id: workId,
            attachment_id: attachmentId,
            message,
            source: readerMessageSource,
          }),
        },
      );

      const responseBody = await response.json();

      if (responseBody.message_sent) {
        infoToast('Message sent');
      } else {
        errorToast('There was an error in sending your message');
      }
    } catch {
      errorToast('There was an error in sending your message');
    }
  };

  return (
    <Modal open onClose={onClose} title="Send a note">
      <ModalContent>
        <Prompt>
          <UserAvatar user={primaryAuthor} />
          <Body size="lg">
            Let {primaryAuthor.displayName} know what sparked your interest in
            downloading this paper
          </Body>
        </Prompt>
        {reactionPrompt ? (
          <div>
            <Body size="md" bold>
              Note from the author:
            </Body>
            <Body size="md">{reactionPrompt}</Body>
          </div>
        ) : null}
        <TextArea
          placeholder={`Dear ${primaryAuthor.displayName},\nWhat sparked my interest was...`}
          rows={4}
          value={message}
          disabled={sending}
          onChange={(event) => {
            setMessage(event.target.value);
          }}
          onKeyDown={(event) => {
            // Check for enter key press with ctrl or cmd
            if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
              // Submit the message, as if we had hit 'Send'
              sendReaderMessage();
            }
          }}
        />
      </ModalContent>
      <ModalButtonGroup>
        <TextButton onClick={onClose}>Cancel</TextButton>
        <Button disabled={sending} onClick={sendReaderMessage}>
          Send
        </Button>
      </ModalButtonGroup>
    </Modal>
  );
};
